import * as Application from 'expo-application'
import * as Updates from 'expo-updates'
import { Platform } from 'react-native'

import { EnvTypes, NFT_CDN_URL, RELAYER_URL, SENTRY_DSN, VELCRO_URL } from '@env'

import appJSON from '../../../../app.json'

export const isTesting = process.env.IS_TESTING === 'true'
export const isDev = process.env.APP_ENV === 'development'
export const isProd = process.env.APP_ENV === 'production'
export const isStaging = process.env.APP_ENV === 'staging'
export const isBenzin = process.env.BENZIN === 'true'
export const isLegends = process.env.LEGENDS === 'true'
/**
 * Ambire Next is a separate production build variant used for beta testing and preview
 * before releasing features to the main production build. It allows us to have two
 * production webkit builds with different branding for testing purposes.
 */
export const isAmbireNext = process.env.AMBIRE_NEXT === 'true'

/**
 * The DEV variant of the mobile-web build. `MOBILE_WEBAPP_DEV` is read from
 * `.env` (loaded into process.env by Expo, babel-inlined) — flip it to "true"
 * there for a dev build that targets `apis.dev.zkxwallet.com` instead of
 * production. ANDed with `MOBILE_WEBAPP` so a leftover "true" in `.env` has no
 * effect on the extension / native builds (which never set MOBILE_WEBAPP).
 */
export const isMobileWebDevBuild =
  process.env.MOBILE_WEBAPP === 'true' && process.env.MOBILE_WEBAPP_DEV === 'true'

export const ZKX_API_PROD_HOST = 'https://apis.zkxwallet.com'
export const ZKX_API_DEV_HOST = 'https://apis.dev.zkxwallet.com'
/** Backend host for ZKX-owned APIs (relayer/velcro/cena/rail-squid/invictus). */
export const ZKX_API_BASE = isMobileWebDevBuild ? ZKX_API_DEV_HOST : ZKX_API_PROD_HOST
/** Repoint a hardcoded prod `apis.zkxwallet.com` URL to the dev host on the dev build. */
export const toZkxApiHost = (url: string): string =>
  isMobileWebDevBuild ? url.replace(ZKX_API_PROD_HOST, ZKX_API_DEV_HOST) : url

/** On Android, this is the package name. On iOS, this is the bundle ID. */
export const APP_ID = Application.applicationId
/**
 * Internal app version, example: 1.0.0 (follows semantic versioning).
 * Fallback to the appJSON version, because in web mode Constants are missing.
 */
export const APP_VERSION = appJSON.version
/**
 * The internal build version of the native build (binary).
 * This is the Info.plist value for `CFBundleVersion` on iOS and
 * the `versionCode` set by `build.gradle` on Android.
 */
export const BUILD_NUMBER = Application.nativeBuildVersion || 'N/A'

export const RELEASE_CHANNEL = Updates.channel || 'N/A'
export const RUNTIME_VERSION = Updates.runtimeVersion || 'N/A'
// FIXME: We should figure out a way to get this,
// because 'expo-constants' uses window refs that break our extension service worker
// export const EXPO_SDK = Constants?.manifest?.sdkVersion || 'N/A'
export const EXPO_SDK = 'N/A'

export const isiOS = Platform.OS === 'ios'
export const isAndroid = Platform.OS === 'android'
export const isMobile = Platform.OS === 'ios' || Platform.OS === 'android'
export const isWeb = Platform.OS === 'web'
export const isMobileWebApp =
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  window.location?.protocol !== 'chrome-extension:'
/**
 * The mobile-web build (`isMobileWebApp`) running inside an actual mobile-device
 * browser (iOS/Android), as opposed to a desktop browser. Detected via userAgent
 * because in react-native-web every browser reports `Platform.OS === 'web'`.
 * iPadOS 13+ masquerades as macOS, so we additionally treat a touch-capable
 * "Macintosh" as mobile. Used to shrink private-mode balance scanning to a single
 * chain on resource-constrained mobile browsers (avoids OOM crashes).
 */
export const isMobileBrowser =
  isMobileWebApp &&
  typeof navigator !== 'undefined' &&
  (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') ||
    (/Macintosh/.test(navigator.userAgent || '') && (navigator as any).maxTouchPoints > 1))

/**
 * The concrete OS of the mobile browser running the mobile-web build, derived
 * from the userAgent (Platform.OS is always 'web' here, so `isiOS`/`isAndroid`
 * are useless on this surface). Returns null on desktop browsers / native.
 * iPadOS 13+ masquerades as "Macintosh", so a touch-capable Mac is treated as
 * iOS. Used to show the OS-specific "Add to Home Screen" install instructions.
 */
export const mobileBrowserOS: 'ios' | 'android' | null = (() => {
  if (!isMobileWebApp || typeof navigator === 'undefined') return null
  const ua = navigator.userAgent || ''
  if (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1)
  )
    return 'ios'
  if (/Android/i.test(ua)) return 'android'
  return null
})()

// eslint-disable-next-line @typescript-eslint/naming-convention
enum APP_ENV {
  PROD = 'production',
  STAGING = 'staging',
  DEV = 'development'
}

interface Config extends EnvTypes {
  APP_ENV: APP_ENV
}

const CONFIG: Config = {
  APP_ENV: APP_ENV.DEV,
  // On the mobile-web DEV build these point at apis.dev.zkxwallet.com; on every
  // other build `toZkxApiHost` is a no-op and the .env values pass through.
  RELAYER_URL: toZkxApiHost(RELAYER_URL),
  VELCRO_URL: toZkxApiHost(VELCRO_URL),
  SENTRY_DSN,
  NFT_CDN_URL: NFT_CDN_URL || 'https://nftcdn.ambire.com',
  ENVIRONMENT: process.env.ENVIRONMENT || 'development',
  DEFAULT_KEYSTORE_PASSWORD_DEV: process.env.DEFAULT_KEYSTORE_PASSWORD_DEV || '',
  LEGENDS_NFT_ADDRESS:
    process.env.LEGENDS_NFT_ADDRESS || '0xF51dF52d0a9BEeB7b6E4B6451e729108a115B863',
  SENTRY_DSN_LEGENDS: process.env.SENTRY_DSN_LEGENDS || '',
  SENTRY_DSN_BROWSER_EXTENSION: process.env.SENTRY_DSN_BROWSER_EXTENSION || '',
  BUNGEE_API_KEY: process.env.BUNGEE_API_KEY || '',
  LI_FI_API_KEY: process.env.LI_FI_API_KEY || ''
}

if (isProd) {
  CONFIG.APP_ENV = APP_ENV.PROD
} else if (isStaging) {
  CONFIG.APP_ENV = APP_ENV.STAGING
}

/**
 * Option to run the app without the ZKX Relayer. See `RELAYER_URL`
 * That's special app mode, which is meant to be turned on only manually.
 * TODO: This is never tested, so it might not work!
 */
export const isRelayerless = !CONFIG.RELAYER_URL

export default CONFIG
